import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../../hooks/auth";
import { canManageSubLogins } from "../../../utils/canManageSubLogins";
import {
  createOwnerSubAccount,
  deleteOwnerSubAccount,
  fetchOwnerPropertiesForAccess,
  fetchOwnerSubAccounts,
  updateOwnerSubAccount,
} from "../services/ownerSubAccountsService";
import {
  IOwnerPropertyOption,
  ISubAccount,
  ISubAccountFormValues,
} from "../types";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

export function useAccessManagement() {
  const { user, access_token } = useAuth();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accounts, setAccounts] = useState<ISubAccount[]>([]);
  const [properties, setProperties] = useState<IOwnerPropertyOption[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ISubAccount | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const [subAccounts, ownerProperties] = await Promise.all([
        fetchOwnerSubAccounts(),
        fetchOwnerPropertiesForAccess(),
      ]);
      setAccounts(subAccounts);
      setProperties(ownerProperties);
    } catch (error) {
      setErrorMessage(
        resolveApiErrorMessage(
          error,
          "Não foi possível carregar os acessos. Tente novamente."
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      history.push("/login", { from: "/access-management" });
      return;
    }

    if (!canManageSubLogins(user)) {
      history.replace("/owner-dashboard");
      return;
    }

    void loadData();
  }, [user, history, loadData, access_token]);

  useEffect(() => {
    if (!feedback) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setFeedback(null);
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [feedback]);

  function openCreateForm() {
    setEditingAccount(null);
    setShowForm(true);
    setFeedback(null);
    setErrorMessage(null);
  }

  function openEditForm(account: ISubAccount) {
    setEditingAccount(account);
    setShowForm(true);
    setFeedback(null);
    setErrorMessage(null);
  }

  function closeForm() {
    setShowForm(false);
    setEditingAccount(null);
  }

  async function handleSubmit(values: ISubAccountFormValues) {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      if (editingAccount) {
        const isLinkedExistingAccount =
          editingAccount.created_for_sub_login === false;

        await updateOwnerSubAccount(
          editingAccount.id,
          isLinkedExistingAccount
            ? {
                is_active: values.active,
                ...(values.assign_all_properties
                  ? { assign_all_properties: true }
                  : { property_ids: values.property_ids }),
              }
            : {
                first_name: values.first_name,
                email: values.email,
                phone: values.phone,
                is_active: values.active,
                ...(values.assign_all_properties
                  ? { assign_all_properties: true }
                  : { property_ids: values.property_ids }),
                ...(values.password
                  ? {
                      password: values.password,
                      password_confirmation: values.password_confirmation,
                    }
                  : {}),
              }
        );
        setFeedback("Acesso atualizado com sucesso.");
      } else {
        await createOwnerSubAccount({
          first_name: values.first_name,
          email: values.email,
          phone: values.phone,
          password: values.password,
          password_confirmation: values.password_confirmation,
          active: values.active,
          ...(values.assign_all_properties
            ? { assign_all_properties: true }
            : { property_ids: values.property_ids }),
        });
        setFeedback("Acesso criado com sucesso.");
      }

      closeForm();
      await loadData();
    } catch (error) {
      setErrorMessage(
        resolveApiErrorMessage(
          error,
          editingAccount
            ? "Não foi possível atualizar o acesso."
            : "Não foi possível criar o acesso."
        )
      );
      throw error;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleActive(account: ISubAccount) {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await updateOwnerSubAccount(account.id, {
        property_ids: account.property_ids,
        is_active: !account.active,
      });
      setFeedback(account.active ? "Acesso desativado." : "Acesso reativado.");
      await loadData();
    } catch (error) {
      setErrorMessage(
        resolveApiErrorMessage(error, "Não foi possível alterar o status do acesso.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(account: ISubAccount): Promise<boolean> {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await deleteOwnerSubAccount(account.id);
      setFeedback("Acesso removido.");
      if (editingAccount?.id === account.id) {
        closeForm();
      }
      await loadData();
      return true;
    } catch (error) {
      setErrorMessage(
        resolveApiErrorMessage(error, "Não foi possível remover o acesso.")
      );
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    loading,
    submitting,
    accounts,
    properties,
    showForm,
    editingAccount,
    feedback,
    errorMessage,
    openCreateForm,
    openEditForm,
    closeForm,
    handleSubmit,
    handleToggleActive,
    handleDelete,
  };
}
